import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Login() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setUnconfirmed(false);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message === "Email not confirmed") {
        setUnconfirmed(true);
      } else {
        toast.error(error.message);
      }
    }
    setSubmitting(false);
  };

  const handleResend = async () => {
    setResending(true);
    const { error } = await supabase.auth.resend({ type: "signup", email });
    if (error) {
      toast.error(error.message);
    } else {
      setResent(true);
    }
    setResending(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">supportfyi</h1>
          <p className="text-sm text-muted-foreground">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setUnconfirmed(false); setResent(false); }}
            required
            autoFocus
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        {unconfirmed && (
          <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm space-y-2">
            <p className="text-muted-foreground">
              Your email address hasn't been confirmed yet.{" "}
              {resent ? (
                <span className="text-foreground font-medium">A new confirmation email has been sent.</span>
              ) : (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="text-foreground font-medium underline underline-offset-2 hover:no-underline disabled:opacity-50"
                >
                  {resending ? "Sending…" : "Resend confirmation email"}
                </button>
              )}
            </p>
          </div>
        )}

        <div className="text-center text-sm text-muted-foreground">
          <Link to="/forgot-password" className="hover:text-foreground transition-colors">
            Forgot password?
          </Link>
          <span className="mx-2">·</span>
          <Link to="/signup" className="hover:text-foreground transition-colors">
            Create account
          </Link>
        </div>
      </div>
    </div>
  );
}
